import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Play, Pause, SkipForward, ChevronRight, Volume2, VolumeX, Headphones } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { PetalIndicator, StateSequence, STATE_COLORS, STATE_NAMES } from '../components/PetalIndicator';
import { MemoryConsolidationClose } from '../components/MemoryConsolidationClose';
import { SCENARIOS, SCENARIO_MODES } from '../data/scenarios';
import { useElevenLabsTTS } from '../hooks/useElevenLabsTTS';
import { useVoiceSettings } from '../hooks/useVoiceSettings';
import { useWakeLock } from '../hooks/useWakeLock';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ScenarioProtocol = ({ user }) => {
  const { scenarioId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const mode = searchParams.get('mode') || 'prepare';

  const scenario = SCENARIOS.find(s => s.id === scenarioId);
  const modeConfig = SCENARIO_MODES[mode];

  const [currentPhase, setCurrentPhase] = useState(0);
  const [timeLeft, setTimeLeft] = useState(scenario?.phases[0]?.duration || 180);
  const [isRunning, setIsRunning] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [showConsolidation, setShowConsolidation] = useState(false);
  const [completedPhases, setCompletedPhases] = useState([]);
  const [sessionNotes, setSessionNotes] = useState('');

  const { speak, speakSequence, stopAudio, isSpeaking, isLoading: ttsLoading } = useElevenLabsTTS();
  const { voiceEnabled, toggleVoice } = useVoiceSettings();
  const { requestWakeLock, releaseWakeLock } = useWakeLock();
  const hasAutoStartedRef = useRef(false);

  const phase = scenario?.phases[currentPhase];

  // Timer
  useEffect(() => {
    if (!isRunning || !phase) return;

    if (timeLeft <= 0) {
      handlePhaseComplete();
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [isRunning, timeLeft, phase]);

  // Wake lock
  useEffect(() => {
    if (isRunning) {
      requestWakeLock();
    } else {
      releaseWakeLock();
    }
    return () => releaseWakeLock();
  }, [isRunning, requestWakeLock, releaseWakeLock]);

  // Auto-start voice
  useEffect(() => {
    if (voiceEnabled && isRunning && phase && !hasAutoStartedRef.current && !isSpeaking && !ttsLoading) {
      hasAutoStartedRef.current = true;
      
      const timer = setTimeout(async () => {
        const introText = `${STATE_NAMES[phase.state]} phase. ${phase.name}. ${phase.instructions.join('. ')}`;
        await speak(introText);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [voiceEnabled, isRunning, phase, isSpeaking, ttsLoading, speak]);

  useEffect(() => {
    hasAutoStartedRef.current = false;
  }, [currentPhase]);

  const handlePhaseComplete = useCallback(() => {
    setCompletedPhases(prev => [...prev, currentPhase]);
    stopAudio();

    if (currentPhase < scenario.phases.length - 1) {
      setCurrentPhase(prev => prev + 1);
      setTimeLeft(scenario.phases[currentPhase + 1].duration);
      hasAutoStartedRef.current = false;
    } else {
      setIsRunning(false);
      setShowConsolidation(true);
    }
  }, [currentPhase, scenario, stopAudio]);

  const handleConsolidationComplete = useCallback(async (data) => {
    // Save to backend
    try {
      await axios.post(`${API}/scenario/complete`, {
        scenario_id: scenarioId,
        mode: mode,
        carry_forward_note: data.carryForwardNote,
        duration_seconds: scenario.duration * 60,
        completed_phases: scenario.phases.length
      }, { withCredentials: true });
    } catch (error) {
      console.error('Failed to save scenario completion:', error);
    }

    setShowConsolidation(false);
    setShowComplete(true);
  }, [scenarioId, mode, scenario]);

  const handlePlayPause = () => {
    if (!isRunning && !hasAutoStartedRef.current) {
      hasAutoStartedRef.current = false;
    }
    setIsRunning(!isRunning);
  };

  const handleSkipPhase = () => {
    stopAudio();
    hasAutoStartedRef.current = true;
    handlePhaseComplete();
  };

  const handleToggleVoice = () => {
    if (isSpeaking || ttsLoading) {
      stopAudio();
    }
    toggleVoice();
    if (!voiceEnabled && isRunning) {
      hasAutoStartedRef.current = false;
    }
  };

  const handleExit = () => {
    stopAudio();
    releaseWakeLock();
    navigate('/scenarios');
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!scenario) {
    return (
      <div className="min-h-screen bg-clarity-bg flex items-center justify-center">
        <p className="text-white">Scenario not found</p>
      </div>
    );
  }

  // Completion Screen
  if (showComplete) {
    return (
      <div className="min-h-screen bg-clarity-bg flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full text-center"
        >
          {/* State Sequence Replay Animation */}
          <div className="flex justify-center gap-4 mb-8">
            {scenario.stateSequence.map((state, index) => (
              <motion.div
                key={state}
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.4 }}
              >
                <PetalIndicator state={state} size="md" completed />
              </motion.div>
            ))}
          </div>

          <h2 className="font-heading text-3xl font-bold text-white mb-4">
            {modeConfig.completionMessage}
          </h2>
          
          <p className="text-slate-400 mb-8">
            {mode === 'prepare' 
              ? "Your nervous system now knows the path. Trust it when stakes are high."
              : "The correction is encoded. Next time, you'll flow differently."}
          </p>

          <div className="glass rounded-xl p-4 mb-8 text-left">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Scenario Completed</p>
            <p className="text-lg text-white font-medium">{scenario.name}</p>
            <p className="text-sm text-slate-400">{mode === 'prepare' ? 'Preparation' : 'Reflection'} Mode</p>
          </div>

          <div className="flex gap-4">
            <Button
              onClick={() => navigate('/scenarios')}
              variant="outline"
              className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              Back to Lab
            </Button>
            <Button
              onClick={() => navigate('/dashboard')}
              className="flex-1 bg-amber-500 text-slate-900 hover:bg-amber-400 font-bold"
            >
              Dashboard
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Memory Consolidation Close
  if (showConsolidation) {
    return (
      <div className="min-h-screen bg-clarity-bg flex items-center justify-center p-4">
        <div className="max-w-lg w-full">
          <MemoryConsolidationClose 
            onComplete={handleConsolidationComplete}
            protocolName={scenario.name}
          />
        </div>
      </div>
    );
  }

  // Main Protocol View
  return (
    <div className="min-h-screen bg-clarity-bg">
      {/* State Progress Bar */}
      <div className="sticky top-0 z-50 bg-clarity-bg/95 backdrop-blur border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <button onClick={handleExit} className="text-slate-400 hover:text-white">
              <X className="w-6 h-6" />
            </button>
            <div className="text-center">
              <p className="text-xs text-slate-500 uppercase tracking-wider">{scenario.name}</p>
              <p className="text-sm text-amber-500">{mode === 'prepare' ? 'Prepare' : 'Reflect'} Mode</p>
            </div>
            <div className="w-6" />
          </div>

          {/* Phase Progress */}
          <div className="flex items-center justify-center gap-2">
            {scenario.phases.map((p, index) => (
              <div key={index} className="flex items-center">
                <div className="flex flex-col items-center">
                  <PetalIndicator 
                    state={p.state}
                    size="sm"
                    active={index === currentPhase}
                    completed={completedPhases.includes(index)}
                  />
                  <span 
                    className="text-[10px] mt-1 uppercase"
                    style={{ 
                      color: index === currentPhase ? STATE_COLORS[p.state] : '#64748b' 
                    }}
                  >
                    {STATE_NAMES[p.state]}
                  </span>
                </div>
                {index < scenario.phases.length - 1 && (
                  <div 
                    className="w-8 h-0.5 mx-1"
                    style={{ 
                      backgroundColor: completedPhases.includes(index) ? '#C8860A' : '#334155' 
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Phase Content */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPhase}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            {/* Phase Header */}
            <div 
              className="rounded-xl p-4 mb-6"
              style={{ backgroundColor: STATE_COLORS[phase.state] }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-mono text-xs uppercase text-white/70">Phase {currentPhase + 1}</p>
                  <h2 className="font-heading text-xl font-bold text-white">{phase.name}</h2>
                </div>
                <div className="text-right">
                  <p className="font-mono text-2xl text-white">{formatTime(timeLeft)}</p>
                  <p className="text-xs text-white/70">{STATE_NAMES[phase.state]} State</p>
                </div>
              </div>
            </div>

            {/* Instructions */}
            <div className="space-y-4 mb-8">
              {phase.instructions.map((instruction, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-4"
                >
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${STATE_COLORS[phase.state]}20` }}
                  >
                    <span className="text-sm font-bold" style={{ color: STATE_COLORS[phase.state] }}>
                      {index + 1}
                    </span>
                  </div>
                  <p className="text-lg text-slate-300 pt-1">{instruction}</p>
                </motion.div>
              ))}
            </div>

            {/* Reflection Prompt */}
            <div className="bg-parchment/10 rounded-xl p-4 border-l-4 border-amber-500">
              <p className="font-mono text-[10px] text-amber-500 uppercase tracking-wider mb-2">
                Reflection Prompt
              </p>
              <p className="text-slate-300 italic">{phase.reflection}</p>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Controls */}
      <div className="fixed bottom-0 left-0 right-0 bg-clarity-bg/95 backdrop-blur border-t border-white/10 p-4 pb-20">
        <div className="max-w-md mx-auto flex items-center justify-center gap-6">
          {/* Voice Toggle */}
          <button
            onClick={handleToggleVoice}
            className={`flex flex-col items-center p-2 rounded-lg transition-all ${
              voiceEnabled ? 'text-amber-500' : 'text-slate-500'
            }`}
          >
            {ttsLoading ? (
              <Headphones className="w-6 h-6 animate-pulse" />
            ) : voiceEnabled ? (
              <Volume2 className={`w-6 h-6 ${isSpeaking ? 'animate-pulse' : ''}`} />
            ) : (
              <VolumeX className="w-6 h-6" />
            )}
            <span className="text-[10px] mt-1">
              {ttsLoading ? 'Loading' : voiceEnabled ? 'Voice ON' : 'Voice OFF'}
            </span>
          </button>

          {/* Play/Pause */}
          <button
            onClick={handlePlayPause}
            className="w-16 h-16 rounded-full flex items-center justify-center transition-all"
            style={{ backgroundColor: STATE_COLORS[phase.state] }}
            data-testid="scenario-play-pause-btn"
          >
            {isRunning ? (
              <Pause className="w-8 h-8 text-white" />
            ) : (
              <Play className="w-8 h-8 text-white ml-1" />
            )}
          </button>

          {/* Skip */}
          <button
            onClick={handleSkipPhase}
            className="flex flex-col items-center p-2 rounded-lg text-slate-500 hover:text-white transition-all"
          >
            <SkipForward className="w-6 h-6" />
            <span className="text-[10px] mt-1">Skip</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScenarioProtocol;
